import { useCallback, useRef, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { TileNotesInput } from '@/types/schema';

import { useGenerateTileNotesSubscription } from '../graphql/TileNotesSummaryPanel.generated';
import { normalizeGeneratedTileNotes } from '../tileNotesMarkdown';

interface GenerationInput {
  input: TileNotesInput[];
  prompt: string;
  matchSummaryFormats: boolean;
}

export interface UseGenerateTileNotesOptions {
  onSuccess: (notes: Record<string, string>) => void;
  onError?: (error: Error) => void;
}

export interface UseGenerateTileNotesReturn {
  generate: (
    input: TileNotesInput[],
    prompt: string,
    matchSummaryFormats?: boolean
  ) => void;
  isGenerating: boolean;
  currentStreamingNote: string | null;
  reset: () => void;
}

export function useGenerateTileNotes({
  onSuccess,
  onError,
}: UseGenerateTileNotesOptions): UseGenerateTileNotesReturn {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [generationInput, setGenerationInput] =
    useState<GenerationInput | null>(null);
  const [currentStreamingNote, setCurrentStreamingNote] = useState<
    string | null
  >(null);

  const successCountRef = useRef(0);
  const errorCountRef = useRef(0);
  const pendingNotesRef = useRef<Record<string, string>>({});

  const isGenerating = !!generationInput;

  const reset = useCallback(() => {
    setGenerationInput(null);
    setCurrentStreamingNote(null);
    successCountRef.current = 0;
    errorCountRef.current = 0;
    pendingNotesRef.current = {};
  }, []);

  useGenerateTileNotesSubscription({
    fetchPolicy: 'no-cache',
    variables: generationInput ?? {
      input: [],
      prompt: '',
      matchSummaryFormats: false,
    },
    skip: !generationInput,
    onData: ({ data }) => {
      const result = data.data?.generateTileNotes;
      if (!result) return;

      if (result.error) {
        errorCountRef.current += 1;
        showFeedback(`Failed to generate notes for tile: ${result.error}`);
        return;
      }

      if (result.notes) {
        setCurrentStreamingNote(result.notes);
      }

      if (result.isComplete && result.notes) {
        successCountRef.current += 1;
        pendingNotesRef.current[result.tileID] = result.notes;
      }
    },
    onComplete: () => {
      const hadSuccess = successCountRef.current > 0;
      const notesToApply = { ...pendingNotesRef.current };
      const count = Object.keys(notesToApply).length;

      reset();

      if (hadSuccess && count > 0) {
        onSuccess(
          Object.fromEntries(
            Object.entries(notesToApply).map(([tileId, notes]) => [
              tileId,
              normalizeGeneratedTileNotes(notes),
            ])
          )
        );
        showFeedback(`Generated AI notes for ${count} tile(s)`, {
          variant: 'success',
        });
      }
    },
    onError: (err) => {
      reset();
      showFeedback('Failed to generate tile notes. Please try again.');
      reportError('Failed to generate tile notes', err);
      onError?.(err);
    },
  });

  const generate = useCallback(
    (
      input: TileNotesInput[],
      prompt: string,
      matchSummaryFormats: boolean = false
    ) => {
      if (input.length === 0) {
        showFeedback('No valid tiles selected');
        return;
      }

      setGenerationInput({
        input,
        prompt,
        matchSummaryFormats,
      });
    },
    [showFeedback]
  );

  return {
    generate,
    isGenerating,
    currentStreamingNote,
    reset,
  };
}
